import { useAuth0 } from '@auth0/auth0-react';
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { treatmentPlanV3Actions } from '../../../../../../store';
import { hasFamilyFieldValue } from '../../../../../common';

import type { AppDispatch, RootState } from '../../../../../../store';
import type { FamilyAdditionalInfoType } from '../../../../../common';

export default function useTreatmentPlanV3FamilyAdditionalInfo() {
  const dispatch = useDispatch<AppDispatch>();
  const { getAccessTokenSilently } = useAuth0();
  const { treatmentPlanId } = useParams<'treatmentPlanId'>();
  const familyPage = useSelector((state: RootState) => state.treatmentPlanV3.familyPage);
  // Covers the whole Family information section: additional-info items and the cultural
  // consideration field (which patches the family via updateTreatmentPlanV3Family).
  const isSaving = useSelector(
    (state: RootState) =>
      state.treatmentPlanV3.createFamilyAdditionalInfoLoading ||
      state.treatmentPlanV3.deleteFamilyAdditionalInfoLoading ||
      state.treatmentPlanV3.updateFamilyLoading,
  );

  const create = useCallback(
    async (infoType: FamilyAdditionalInfoType, value: string) => {
      if (!treatmentPlanId) return false;
      const result = await dispatch(
        treatmentPlanV3Actions.createTreatmentPlanV3FamilyAdditionalInfo({
          treatmentPlanId,
          data: { info_type: infoType, value },
          getAccessTokenSilently,
        }),
      );
      return treatmentPlanV3Actions.createTreatmentPlanV3FamilyAdditionalInfo.fulfilled.match(
        result,
      );
    },
    [dispatch, getAccessTokenSilently, treatmentPlanId],
  );

  const remove = useCallback(
    async (infoType: FamilyAdditionalInfoType, itemId: number) => {
      if (!treatmentPlanId) return false;
      const result = await dispatch(
        treatmentPlanV3Actions.deleteTreatmentPlanV3FamilyAdditionalInfo({
          treatmentPlanId,
          infoType,
          itemId,
          getAccessTokenSilently,
        }),
      );
      return treatmentPlanV3Actions.deleteTreatmentPlanV3FamilyAdditionalInfo.fulfilled.match(
        result,
      );
    },
    [dispatch, getAccessTokenSilently, treatmentPlanId],
  );

  return {
    additionalInfo: {
      maternal_medical_history: familyPage?.maternal_medical_history ?? [],
      paternal_medical_history: familyPage?.paternal_medical_history ?? [],
      community_support: familyPage?.community_support ?? [],
    },
    hasSavedValues: hasFamilyFieldValue(familyPage),
    isSaving,
    create,
    remove,
  };
}
